'use client';

import { useMemo, useState } from 'react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { cn, numberFormatter } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import useSmartTrade, { type SmartTradeItem } from '../../hooks/use-smart-trade';
import CircleDollarBuy from '@/components/Icons/CircleDollarBuy';
import CircleDollarSell from '@/components/Icons/CircleDollarSell';
import useTopHolder, { type TopHolderToken } from '../../hooks/use-top-holder';
import type { PolymarketMarket } from './types';
import { isEmpty } from 'lodash';
import { formatCompactValue } from '@/lib/utils/number';

type PositionDirection = 'profit' | 'loss';
type TradeSide = 'yes' | 'no';
type TradeAction = 'Buy' | 'Sell';
type HolderSide = 'yes' | 'no';

export interface TraderSummaryItem {
  id: string;
  title: string;
  value: string;
  description: string;
  direction: PositionDirection;
}

export interface WhaleTradeItem {
  id: string;
  hoursAgo: string;
  tradeTime: string;
  trader: string;
  side: TradeSide;
  action: TradeAction;
  price: string;
  usdAmount: string;
  share: string;
}

export interface HolderDistributionItem {
  id: string;
  holder: string;
  marketShare: string;
  rank: number;
  color: string;
  shares?: string;
}

export interface HolderDistribution {
  side: HolderSide;
  title: string;
  topShareLabel: string;
  othersLabel: string;
  holders: HolderDistributionItem[];
}

export interface WinnersLosersItem {
  id: string;
  name: string;
  value: string;
  barPercent: number;
}

export interface WinnersLosersGroup {
  id: string;
  title: string;
  color: string;
  items: WinnersLosersItem[];
}

interface MarketTradersProps {
  eventSlug: string;
  markets: PolymarketMarket[];
  summaryItems?: TraderSummaryItem[];
  whaleTrades?: WhaleTradeItem[];
  winnersLosersGroups?: WinnersLosersGroup[];
  winnersLosersGroupsByDate?: Record<string, WinnersLosersGroup[]>;
  holderDistributions?: HolderDistribution[];
  holderDistributionsByDate?: Record<string, HolderDistribution[]>;
  className?: string;
}

const DEFAULT_SUMMARY_ITEMS: TraderSummaryItem[] = [
  {
    id: 'yes-profit',
    title: 'Yes $ Positions in Profit',
    value: '$25',
    description: '1 users (3.2%) making money',
    direction: 'profit',
  },
  {
    id: 'yes-loss',
    title: 'Yes $ Positions in Loss',
    value: '$210',
    description: '30 users (96.8%) losing money',
    direction: 'loss',
  },
  {
    id: 'no-profit',
    title: 'No $ Positions in Profit',
    value: '$4,093',
    description: '25 users (78.1%) making money',
    direction: 'profit',
  },
  {
    id: 'no-loss',
    title: 'No $ Positions in Loss',
    value: '$55',
    description: '7 users (21.9%) losing money',
    direction: 'loss',
  },
];

const DEFAULT_WHALE_TRADES: WhaleTradeItem[] = [
  {
    id: 'trade-1',
    hoursAgo: '30m',
    tradeTime: '2025-01-11 00:20',
    trader: '0x04fa...f7d4',
    side: 'yes',
    action: 'Buy',
    price: '0.97',
    usdAmount: '$14,937',
    share: '365',
  },
  {
    id: 'trade-2',
    hoursAgo: '2h',
    tradeTime: '2025-01-11 00:20',
    trader: '0x5b65...c1b4',
    side: 'no',
    action: 'Buy',
    price: '0.37',
    usdAmount: '$465.29',
    share: '365',
  },
  {
    id: 'trade-3',
    hoursAgo: '6h',
    tradeTime: '2025-01-11 00:20',
    trader: '0x9b36...a1b4',
    side: 'yes',
    action: 'Sell',
    price: '0.42',
    usdAmount: '$216.18',
    share: '360',
  },
  {
    id: 'trade-4',
    hoursAgo: '7h',
    tradeTime: '2025-02-13 11:28',
    trader: '0x93dd...d5e1',
    side: 'no',
    action: 'Buy',
    price: '0.14',
    usdAmount: '$891.47',
    share: '370',
  },
  {
    id: 'trade-5',
    hoursAgo: '1d',
    tradeTime: '2025-01-02 11:43',
    trader: '0xc4d5...8b04',
    side: 'yes',
    action: 'Buy',
    price: '0.21',
    usdAmount: '$751.77',
    share: '625',
  },
  {
    id: 'trade-6',
    hoursAgo: '2d',
    tradeTime: '2025-01-02 11:43',
    trader: '0x7e9f...a6b3',
    side: 'no',
    action: 'Sell',
    price: '0.89',
    usdAmount: '$360.72',
    share: '450',
  },
  {
    id: 'trade-7',
    hoursAgo: '2d',
    tradeTime: '2024-11-30 10:20',
    trader: '0xd7c9...4f14',
    side: 'yes',
    action: 'Buy',
    price: '0.41',
    usdAmount: '$120.00',
    share: '230',
  },
];

const DEFAULT_HOLDER_DATES = ['April 21', 'April 30', 'May 31', 'Jun 30'];

const DEFAULT_WINNERS_LOSERS_GROUPS: WinnersLosersGroup[] = [
  {
    id: 'profits',
    title: 'Top Trader Total Profits',
    color: '#22C55E',
    items: [
      { id: 'profit-1', name: '0xB9...4680', value: '$14,584', barPercent: 99 },
      { id: 'profit-2', name: 'Fortunately...', value: '$13,225', barPercent: 100 },
      { id: 'profit-3', name: 'onekey09', value: '$11,351', barPercent: 82 },
      { id: 'profit-4', name: 'cqs', value: '$10,414', barPercent: 75 },
      { id: 'profit-5', name: 'momom', value: '$8,759', barPercent: 63 },
      { id: 'profit-6', name: 'videlake', value: '$5,765', barPercent: 42 },
      { id: 'profit-7', name: 'NeH', value: '$5,564', barPercent: 40 },
      { id: 'profit-8', name: 'ilh', value: '$4,445', barPercent: 32 },
      { id: 'profit-9', name: 'linghujunlan', value: '$4,207', barPercent: 30 },
      { id: 'profit-10', name: 'pq18503', value: '$3,464', barPercent: 25 },
    ],
  },
  {
    id: 'losses',
    title: 'Top Trader Total Losses',
    color: '#EF4444',
    items: [
      { id: 'loss-1', name: 'comon119', value: '$53,612', barPercent: 100 },
      { id: 'loss-2', name: 'seems1126', value: '$18,395', barPercent: 37 },
      { id: 'loss-3', name: 'caspersmc', value: '$9,946', barPercent: 20 },
      { id: 'loss-4', name: 'lol9009', value: '$6,231', barPercent: 13 },
      { id: 'loss-5', name: 'artus33', value: '$6,036', barPercent: 12 },
      { id: 'loss-6', name: 'expectless', value: '$5,500', barPercent: 11 },
      { id: 'loss-7', name: 'qqshia', value: '$4,093', barPercent: 8 },
      { id: 'loss-8', name: 'vk7', value: '$3,866', barPercent: 8 },
      { id: 'loss-9', name: '0x31...408f', value: '$1,893', barPercent: 4 },
      { id: 'loss-10', name: 'bowenbal', value: '$1,886', barPercent: 4 },
    ],
  },
];

const DEFAULT_WINNERS_LOSERS_GROUPS_BY_DATE = createWinnersLosersGroupsByDate(
  DEFAULT_HOLDER_DATES,
  DEFAULT_WINNERS_LOSERS_GROUPS,
);

const DEFAULT_HOLDER_DISTRIBUTIONS: HolderDistribution[] = [
  {
    side: 'yes',
    title: 'Top Yes Holders',
    topShareLabel: 'Top 10 Holders 55.6%',
    othersLabel: 'Others 45.4%',
    holders: [
      {
        id: 'yes-1',
        holder: '0xd7b4...5b01',
        marketShare: '8.9%',
        rank: 1,
        color: '#0D6938',
        shares: '8010',
      },
      { id: 'yes-2', holder: '0xa8b7...5b01', marketShare: '6.8%', rank: 2, color: '#118544' },
      { id: 'yes-3', holder: '0x8bb4...5b01', marketShare: '5.2%', rank: 3, color: '#14A855' },
      { id: 'yes-4', holder: '0xb886...5b01', marketShare: '4.8%', rank: 4, color: '#22C55E' },
      { id: 'yes-5', holder: '0xec82...5b01', marketShare: '4.2%', rank: 5, color: '#4ADE80' },
      { id: 'yes-6', holder: '0x6a15...5b01', marketShare: '3.5%', rank: 6, color: '#86EFAC' },
      { id: 'yes-7', holder: '0x01f4...5b01', marketShare: '3.2%', rank: 7, color: '#A7F3D0' },
      { id: 'yes-8', holder: '0xf458...5b01', marketShare: '2.9%', rank: 8, color: '#C6F6D5' },
      { id: 'yes-9', holder: '0xa58d...5b01', marketShare: '2.6%', rank: 9, color: '#D1FAE5' },
      { id: 'yes-10', holder: '0xa58d...5b01', marketShare: '2.3%', rank: 10, color: '#D1FAE5' },
    ],
  },
  {
    side: 'no',
    title: 'Top No Holders',
    topShareLabel: 'Top 10 Holders 58%',
    othersLabel: 'Others 42%',
    holders: [
      {
        id: 'no-1',
        holder: '0x919d...5b01',
        marketShare: '21%',
        rank: 1,
        color: '#DC2626',
        shares: '18900',
      },
      { id: 'no-2', holder: '0xc446...6KDK', marketShare: '13%', rank: 2, color: '#EF4444' },
      { id: 'no-3', holder: '0x0711...89Ib', marketShare: '9%', rank: 3, color: '#F87171' },
      { id: 'no-4', holder: '0x4faA...0cbk', marketShare: '7%', rank: 4, color: '#FCA5A5' },
      { id: 'no-5', holder: '0xb7b0...bds9', marketShare: '5%', rank: 5, color: '#FECACA' },
      { id: 'no-6', holder: '0x61a9...m788', marketShare: '3%', rank: 6, color: '#FEE2E2' },
    ],
  },
];

const DEFAULT_HOLDER_DISTRIBUTIONS_BY_DATE = createHolderDistributionsByDate(
  DEFAULT_HOLDER_DATES,
  DEFAULT_HOLDER_DISTRIBUTIONS,
);

const HOLDER_COLORS: Record<HolderSide, string[]> = {
  yes: [
    '#0D6938',
    '#118544',
    '#14A855',
    '#22C55E',
    '#4ADE80',
    '#86EFAC',
    '#A7F3D0',
    '#C6F6D5',
    '#D1FAE5',
    '#D1FAE5',
  ],
  no: [
    '#DC2626',
    '#EF4444',
    '#F87171',
    '#FCA5A5',
    '#FECACA',
    '#FEE2E2',
    '#FEE2E2',
    '#FEE2E2',
    '#FEE2E2',
    '#FEE2E2',
  ],
};

const TRADE_COLUMNS =
  'grid-cols-[minmax(56px,0.6fr)_minmax(156px,1.6fr)_minmax(176px,1.8fr)_minmax(64px,0.7fr)_minmax(68px,0.7fr)_minmax(76px,0.8fr)_minmax(104px,1fr)_minmax(72px,0.7fr)]';

export default function MarketTraders({
  eventSlug,
  markets,
  summaryItems = DEFAULT_SUMMARY_ITEMS,
  whaleTrades = DEFAULT_WHALE_TRADES,
  winnersLosersGroups = DEFAULT_WINNERS_LOSERS_GROUPS,
  winnersLosersGroupsByDate = DEFAULT_WINNERS_LOSERS_GROUPS_BY_DATE,
  holderDistributions = DEFAULT_HOLDER_DISTRIBUTIONS,
  holderDistributionsByDate = DEFAULT_HOLDER_DISTRIBUTIONS_BY_DATE,
  className,
}: MarketTradersProps) {
  const { t } = useTranslation();

  return (
    <section className={cn('mt-5 text-white', className)}>
      <h2 className="mb-4 text-lg font-semibold leading-6 text-white">{t('Traders')}</h2>

      <SmartTradersTable eventSlug={eventSlug} whaleTrades={whaleTrades} />

      <TopHoldersDistribution
        markets={markets}
        distributions={holderDistributions}
        distributionsByDate={holderDistributionsByDate}
      />
      {/* <WinnersLosers
        markets={markets}
        groups={winnersLosersGroups}
        groupsByDate={winnersLosersGroupsByDate}
      /> */}
    </section>
  );
}

function SmartTradersTable({
  eventSlug,
  whaleTrades,
}: {
  eventSlug: string;
  whaleTrades: WhaleTradeItem[];
}) {
  const { t } = useTranslation();
  const { data: smartTradeResponse, trades } = useSmartTrade({ eventSlug });
  const displayTrades = useMemo(
    () => (smartTradeResponse ? trades.map(mapSmartTradeToWhaleTrade) : whaleTrades),
    [smartTradeResponse, trades, whaleTrades],
  );

  if (isEmpty(displayTrades)) return null;

  return (
    <div className="mt-8 space-y-3">
      <div className="space-y-1">
        <h3 className="text-base font-normal leading-7 text-[#B7B9BC] md:text-sm md:font-semibold md:leading-6">
          {t('Smart Traders')}
        </h3>
        <p className="max-w-103 text-xs leading-4.5 text-[#999999] md:max-w-none">
          {t('Showing the top 1% largest trades (by USD) for both YES and NO sides.')}
        </p>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-210">
          <TradeHeader />
          <div className="space-y-1">
            {displayTrades.map((trade) => (
              <TradeRow key={trade.id} trade={trade} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TraderStatCard({ item }: { item: TraderSummaryItem }) {
  const isProfit = item.direction === 'profit';
  const Icon = isProfit ? ArrowUpRight : ArrowDownRight;
  const { t } = useTranslation();

  return (
    <div className="min-h-27.5 rounded-lg bg-linear-[176.7deg,#202020_6%,#0B0B0B_94%] px-2.5 py-4 md:px-4">
      <div className="flex min-w-0 items-center gap-2">
        <span className="min-w-0 truncate text-[13px] leading-5.5 text-[#757575] md:text-sm md:text-[#999999]">
          {t(item.title)}
        </span>
        <Icon
          size={16}
          strokeWidth={1.5}
          className={cn('shrink-0', isProfit ? 'text-[#14C784]' : 'text-[#F73538]')}
        />
      </div>
      <div className="mt-2.5 text-lg font-medium leading-5.5 text-white">{item.value}</div>
      <div className="mt-2.5 text-xs leading-5 text-white">{t(item.description)}</div>
    </div>
  );
}

function TradeHeader() {
  const { t } = useTranslation();
  const labels = [
    'Hours Ago',
    'Trade Time',
    'Trader',
    'Sides',
    'Action',
    'Price',
    'USD Amount',
    'Shares',
  ];

  return (
    <div
      className={cn(
        'grid h-10.5 items-center border-b border-[#222222] text-[11px] font-medium leading-4 text-[#666666]',
        TRADE_COLUMNS,
      )}
    >
      {labels.map((label) => (
        <div key={label} className="truncate">
          {t(label)}
        </div>
      ))}
    </div>
  );
}

function TradeRow({ trade }: { trade: WhaleTradeItem }) {
  return (
    <div
      className={cn(
        'grid h-10.75 items-center border-b border-[#1A1A1A] text-xs leading-4.5 text-white last:border-b-0',
        TRADE_COLUMNS,
      )}
    >
      <Cell strong>{trade.hoursAgo}</Cell>
      <Cell className="text-[11px] font-normal">{trade.tradeTime}</Cell>
      <Cell>{trade.trader}</Cell>
      <div>
        <SideBadge side={trade.side} />
      </div>
      <Cell strong className="flex items-center gap-1.5">
        {trade.action === 'Buy' ? (
          <CircleDollarBuy className="size-4 text-[#73B4FF]" />
        ) : (
          <CircleDollarSell className="size-4 text-[#8CFF19]" />
        )}
        {trade.action}
      </Cell>
      <div className="flex items-center gap-1.5">
        <span className="truncate font-semibold">{trade.price}</span>
      </div>
      <Cell strong>{trade.usdAmount}</Cell>
      <Cell strong>{trade.share}</Cell>
    </div>
  );
}

function Cell({
  children,
  strong,
  className,
}: {
  children: React.ReactNode;
  strong?: boolean;
  className?: string;
}) {
  return (
    <div className={cn('truncate', strong ? 'font-medium' : 'font-normal', className)}>
      {children}
    </div>
  );
}

function SideBadge({ side }: { side: TradeSide }) {
  const isYes = side === 'yes';
  const label = isYes ? 'Yes' : 'No';
  const { t } = useTranslation();

  return (
    <span
      className={cn(
        'inline-flex h-4.5 min-w-8.5 items-center justify-center rounded px-1.5 text-xs font-medium leading-4.5',
        isYes ? 'bg-[#14C784]/35 text-[#14C784]' : 'bg-[#FF2D2D]/35 text-[#FF2D2D]',
      )}
    >
      {t(label)}
    </span>
  );
}

function WinnersLosers({
  markets,
  groups,
  groupsByDate,
}: {
  markets: PolymarketMarket[];
  groups: WinnersLosersGroup[];
  groupsByDate?: Record<string, WinnersLosersGroup[]>;
}) {
  const { t } = useTranslation();
  const marketTabItems = useMemo(() => getMarketTabItems(markets), [markets]);
  const [activeMarketId, setActiveMarketId] = useState(marketTabItems[0]?.value ?? '');
  const selectedMarketId = marketTabItems.some((item) => item.value === activeMarketId)
    ? activeMarketId
    : (marketTabItems[0]?.value ?? '');
  const activeGroups = groupsByDate?.[selectedMarketId] ?? groups;

  if (!activeGroups.length) return null;

  return (
    <div className="mt-8 space-y-6">
      <div className="space-y-4">
        <h3 className="text-sm font-semibold leading-5 text-[#B7B9BC]">{t('Winners & Losers')}</h3>
        {marketTabItems.length > 1 && (
          <DateTabs
            items={marketTabItems}
            activeValue={selectedMarketId}
            onValueChange={setActiveMarketId}
            size="compact"
          />
        )}
      </div>

      <div className="grid gap-6 grid-cols-2">
        {activeGroups.map((group) => (
          <WinnersLosersColumn key={group.id} group={group} />
        ))}
      </div>
    </div>
  );
}

function WinnersLosersColumn({ group }: { group: WinnersLosersGroup }) {
  const { t } = useTranslation();

  return (
    <div className="min-w-0">
      <h4 className="mb-3 text-sm font-normal leading-5 text-[#B7B9BC]">{t(group.title)}</h4>
      <div>
        {group.items.map((item) => (
          <div
            key={item.id}
            className="grid h-10 grid-cols-[93px_minmax(0,1fr)_56px] items-center gap-2.5 py-2"
          >
            <div className="truncate text-sm font-medium leading-5 text-[#757575]">{item.name}</div>
            <div className="h-4 min-w-0 overflow-hidden">
              <div
                className="h-full"
                style={{
                  width: `${Math.min(Math.max(item.barPercent, 0), 100)}%`,
                  backgroundColor: group.color,
                }}
              />
            </div>
            <div className="truncate text-xs font-normal leading-5 text-white">{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopHoldersDistribution({
  markets,
  distributions,
  distributionsByDate,
}: {
  markets: PolymarketMarket[];
  distributions: HolderDistribution[];
  distributionsByDate?: Record<string, HolderDistribution[]>;
}) {
  const { t } = useTranslation();
  const marketTabItems = useMemo(() => getMarketTabItems(markets), [markets]);
  const [activeMarketId, setActiveMarketId] = useState(marketTabItems[0]?.value ?? '');
  const selectedMarketId = marketTabItems.some((item) => item.value === activeMarketId)
    ? activeMarketId
    : (marketTabItems[0]?.value ?? '');
  const activeConditionIds = selectedMarketId ? [selectedMarketId] : [];

  const { topHolders } = useTopHolder({
    conditionIds: activeConditionIds,
    enabled: activeConditionIds.length > 0,
  });
  const activeTokens = useMemo(
    () =>
      topHolders.find((holder) => holder.condition_id === selectedMarketId)?.tokens ??
      topHolders[0]?.tokens ??
      [],
    [selectedMarketId, topHolders],
  );
  const tokenDistributions = useMemo(
    () => mapTopHolderTokensToDistributions(activeTokens),
    [activeTokens],
  );
  const activeDistributions =
    tokenDistributions.length > 0
      ? tokenDistributions
      : (distributionsByDate?.[selectedMarketId] ?? distributions);

  if (!activeDistributions.length) return null;

  return (
    <div className="@container mt-8 space-y-10">
      <div className="space-y-6">
        <h3 className="text-[26px] font-semibold leading-8 text-[#B7B9BC] md:text-sm md:leading-5">
          {t('Top Holders Distribution')}
        </h3>
        {marketTabItems.length > 1 && (
          <DateTabs
            items={marketTabItems}
            activeValue={selectedMarketId}
            onValueChange={setActiveMarketId}
            size="large"
          />
        )}
      </div>

      <div className="grid gap-12 grid-cols-2 @max-2xl:grid-cols-1">
        {activeDistributions.map((distribution) => (
          <HolderDistributionPanel key={distribution.side} distribution={distribution} />
        ))}
      </div>
    </div>
  );
}

function DateTabs({
  items,
  activeValue,
  onValueChange,
  size = 'compact',
}: {
  items: { value: string; label: string }[];
  activeValue?: string;
  onValueChange?: (value: string) => void;
  size?: 'compact' | 'large';
}) {
  if (!items.length) return null;

  const isLarge = size === 'large';
  const selectedValue = activeValue ?? items[0]?.value;

  return (
    <div className="max-w-full overflow-x-auto overflow-y-visible">
      <div
        className={cn(
          'flex w-max items-center font-medium text-[#757575]',
          isLarge
            ? 'h-7.5 text-[26px] leading-7 md:text-sm md:leading-4.5'
            : 'h-6.5 text-sm leading-4.5',
        )}
      >
        {items.map((item, index) => (
          <button
            key={item.value}
            type="button"
            onClick={() => onValueChange?.(item.value)}
            className={cn(
              'relative cursor-pointer text-left transition-colors hover:text-white first:pl-0 last:pr-0',
              isLarge ? 'px-7 md:px-3' : 'px-3',
              selectedValue === item.value && 'text-white',
              index > 0 && 'border-l border-[#1F1F1F]',
            )}
          >
            <span className="relative inline-block whitespace-nowrap">
              {item.label}
              {selectedValue === item.value && (
                <span
                  className={cn(
                    'absolute left-0 right-0 rounded bg-[#A772FF]',
                    isLarge ? '-bottom-5 h-1.5 md:-bottom-1 md:h-0.5' : '-bottom-1 h-0.5',
                  )}
                />
              )}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function HolderDistributionPanel({ distribution }: { distribution: HolderDistribution }) {
  return (
    <div className="relative grid items-center gap-8 grid-cols-2">
      <HolderDonut distribution={distribution} />
      <HolderRankList distribution={distribution} />
    </div>
  );
}

function HolderDonut({ distribution }: { distribution: HolderDistribution }) {
  const { t } = useTranslation();
  const holders = distribution.holders;
  const othersPercent = parsePercent(distribution.othersLabel);
  const segments = [
    {
      id: `${distribution.side}-others`,
      percent: othersPercent,
      color: '#1E293B',
    },
    ...holders.map((holder) => ({
      id: holder.id,
      percent: parsePercent(holder.marketShare),
      color: holder.color,
    })),
  ];

  return (
    <div className="flex justify-center md:justify-start">
      <div className="relative size-85 md:size-60">
        <svg className="size-full" viewBox="0 0 240 240" aria-hidden>
          <g transform="rotate(-180 120 120)">
            {segments.reduce<React.ReactNode[]>((nodes, segment, index) => {
              const start = segments.slice(0, index).reduce((sum, item) => sum + item.percent, 0);
              nodes.push(
                <DonutSegment
                  key={segment.id}
                  color={segment.color}
                  startPercent={start}
                  percent={segment.percent}
                />,
              );
              return nodes;
            }, [])}
          </g>
          <circle cx="120" cy="120" r="68" fill="#000000" />
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="text-[24px] font-semibold leading-7 text-[#8B8B8B] md:text-sm md:leading-5">
            {t('Top 10 Holders')}
          </div>
          {/* <div className="text-[30px] font-bold leading-9 text-white md:text-base md:leading-5">
            {formatCenterPercent(distribution.topShareLabel)}
          </div>
          <div className="mt-6 text-[24px] font-semibold leading-7 text-[#8B8B8B] md:mt-3 md:text-sm md:leading-5">
            {t('Others')}
          </div>
          <div className="text-[30px] font-bold leading-9 text-white md:text-base md:leading-5">
            {formatCenterPercent(distribution.othersLabel)}
          </div> */}
        </div>
      </div>
    </div>
  );
}

function DonutSegment({
  color,
  startPercent,
  percent,
}: {
  color: string;
  startPercent: number;
  percent: number;
}) {
  const radius = 91;
  const circumference = 2 * Math.PI * radius;
  const gap = 0.45;
  const visiblePercent = Math.max(percent - gap, 0);
  const dash = (visiblePercent / 100) * circumference;
  const offset = -((startPercent + gap / 2) / 100) * circumference;

  return (
    <circle
      cx="120"
      cy="120"
      r={radius}
      fill="none"
      stroke={color}
      strokeWidth="50"
      strokeDasharray={`${dash} ${circumference - dash}`}
      strokeDashoffset={offset}
    />
  );
}

function HolderRankList({ distribution }: { distribution: HolderDistribution }) {
  const { t } = useTranslation();

  return (
    <div className="min-w-0 self-start">
      <h4 className="mb-6 text-[26px] font-semibold leading-8 text-[#B7B9BC] md:mb-3 md:text-sm md:font-normal md:leading-5">
        {t(distribution.title)}
      </h4>
      <div>
        {distribution.holders.map((holder) => (
          <div
            key={holder.id}
            className="group relative grid cursor-default grid-cols-[44px_80px_minmax(0,1fr)] items-center border-b border-[#1F1F1F] py-2.5 text-[24px] font-semibold leading-7 transition-colors hover:bg-white/5 md:grid-cols-[32px_64px_minmax(0,1fr)] md:py-2 md:text-xs md:leading-4"
          >
            <span style={{ color: holder.color }}>{holder.rank}</span>
            <span style={{ color: holder.color }}>{holder.shares}</span>
            <span className="truncate text-[#B7B9BC]">{holder.holder}</span>
            {/* <HolderInfoCard
              holder={holder}
              placement={distribution.side === 'no' ? 'left' : 'right'}
            /> */}
          </div>
        ))}
      </div>
    </div>
  );
}

function HolderInfoCard({
  holder,
  placement,
}: {
  holder: HolderDistributionItem;
  placement: 'left' | 'right';
}) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'pointer-events-none absolute top-1/2 z-10 hidden h-37.5 w-56.25 -translate-y-1/2 rounded-[10px] bg-[#171717] p-4 shadow-2xl xl:group-hover:block',
        placement === 'left' ? 'right-full mr-4' : 'left-full ml-4',
      )}
    >
      <div className="truncate text-sm font-medium leading-4 text-white">{holder.holder}</div>
      <div className="mt-6 grid grid-cols-[1fr_auto] gap-y-2 text-xs leading-4.5">
        <span className="text-[#757575]">{t('Shares')}</span>
        <span className="text-sm leading-4.5 text-white">{holder.shares || '18900'}</span>
        <span className="text-[#757575]">{t('Market Share')}</span>
        <span className="text-sm leading-4.5 text-white">{holder.marketShare}</span>
        <span className="text-[#757575]">{t('Holder Rank')}</span>
        <span className="text-sm leading-4.5 text-white">#{holder.rank}</span>
      </div>
      {/* <div className="mt-4 text-xs leading-5.5 text-[#A772FF]">More</div> */}
    </div>
  );
}

function createWinnersLosersGroupsByDate(dates: string[], groups: WinnersLosersGroup[]) {
  return dates.reduce<Record<string, WinnersLosersGroup[]>>((dataByDate, date, dateIndex) => {
    const dateFactor = 1 - dateIndex * 0.08;

    dataByDate[date] = groups.map((group) => ({
      ...group,
      items: group.items.map((item, itemIndex) => {
        const rowFactor = dateFactor + itemIndex * 0.006;

        return {
          ...item,
          value: scaleCurrency(item.value, rowFactor),
          barPercent: clamp(item.barPercent * rowFactor, 4, 100),
        };
      }),
    }));

    return dataByDate;
  }, {});
}

function createHolderDistributionsByDate(dates: string[], distributions: HolderDistribution[]) {
  return dates.reduce<Record<string, HolderDistribution[]>>((dataByDate, date, dateIndex) => {
    const dateFactor = 1 - dateIndex * 0.05;

    dataByDate[date] = distributions.map((distribution) => {
      const topShare = clamp(parsePercent(distribution.topShareLabel) * dateFactor, 1, 95);
      const othersShare = clamp(100 - topShare, 5, 99);

      return {
        ...distribution,
        topShareLabel: `Top 10 Holders ${formatPercent(topShare)}`,
        othersLabel: `Others ${formatPercent(othersShare)}`,
        holders: distribution.holders.map((holder, holderIndex) => {
          const holderFactor = dateFactor + holderIndex * 0.01;

          return {
            ...holder,
            marketShare: formatPercent(
              clamp(parsePercent(holder.marketShare) * holderFactor, 0.1, 95),
            ),
            shares: holder.shares
              ? String(Math.round(Number(holder.shares) * holderFactor))
              : holder.shares,
          };
        }),
      };
    });

    return dataByDate;
  }, {});
}

function scaleCurrency(value: string, factor: number) {
  const amount = Number(value.replace(/[^0-9.]/g, ''));

  if (!Number.isFinite(amount)) return value;

  return `$${Math.round(amount * factor).toLocaleString('en-US')}`;
}

function formatPercent(value: number) {
  const rounded = Math.round(value * 10) / 10;

  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function parsePercent(value: string) {
  const match = value.match(/(\d+(?:\.\d+)?)%/);
  return match ? Number(match[1]) : 0;
}

function formatCenterPercent(value: string) {
  const match = value.match(/(\d+(?:\.\d+)?%)/);
  return match ? match[1] : value;
}

function getMarketTabItems(markets: PolymarketMarket[]) {
  return markets
    .map((market) => ({
      value: market.condition_id?.trim(),
      label:
        market.group_item_title?.trim() ||
        market.market_question?.trim() ||
        market.market_slug?.trim() ||
        market.condition_id?.trim(),
    }))
    .filter((item): item is { value: string; label: string } => Boolean(item.value && item.label));
}

function mapTopHolderTokensToDistributions(tokens: TopHolderToken[]): HolderDistribution[] {
  return tokens
    .filter((token): token is TopHolderToken & { outcome: HolderSide } =>
      isHolderSide(token.outcome),
    )
    .map((token) => {
      const totalAmount = token.holders.reduce((sum, holder) => sum + holder.amount, 0);
      const colors = HOLDER_COLORS[token.outcome];
      const holders = token.holders.slice(0, 10).map((holder, index) => {
        const marketShare = normalizeHolderPercent(holder.percentage, holder.amount, totalAmount);

        return {
          id: `${token.token}-${holder.wallet || index}`,
          holder: holder.name || formatTraderAddress(holder.wallet),
          marketShare: formatPercent(marketShare),
          rank: index + 1,
          color: colors[index] ?? colors[colors.length - 1],
          shares: formatCompactValue(holder.amount),
        };
      });
      const topShare =
        normalizeTokenPercent(token.top10_percentage) ??
        holders.reduce((sum, holder) => sum + parsePercent(holder.marketShare), 0);
      const clampedTopShare = clamp(topShare, 0, 100);

      return {
        side: token.outcome,
        title: token.outcome === 'yes' ? 'Top Yes Holders' : 'Top No Holders',
        topShareLabel: `Top 10 Holders ${formatPercent(clampedTopShare)}`,
        othersLabel: `Others ${formatPercent(100 - clampedTopShare)}`,
        holders,
      };
    });
}

function isHolderSide(value: string): value is HolderSide {
  return value === 'yes' || value === 'no';
}

function normalizeHolderPercent(value: number | null, amount: number, totalAmount: number) {
  const normalizedValue = normalizeTokenPercent(value);

  if (normalizedValue !== null) return normalizedValue;
  if (!Number.isFinite(amount) || !Number.isFinite(totalAmount) || totalAmount <= 0) return 0;

  return (amount / totalAmount) * 100;
}

function normalizeTokenPercent(value: number | null) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;

  return value > 0 && value <= 1 ? value * 100 : value;
}

function mapSmartTradeToWhaleTrade(trade: SmartTradeItem, index: number): WhaleTradeItem {
  const time = trade.block_time;
  const side = normalizeTradeSide(trade.token_outcome);
  const action = normalizeTradeAction(trade.side);

  return {
    id: `${trade.maker}-${time}-${index}`,
    hoursAgo: formatRelativeTradeTime(time),
    tradeTime: formatTradeTime(time),
    trader: formatTraderAddress(trade.maker),
    side,
    action,
    price: formatPrice(trade.price),
    usdAmount: formatCurrency(trade.amount),
    share: numberFormatter(trade.shares, { notation: 'compact' }),
  };
}

function normalizeTradeSide(value: string | null): TradeSide {
  return value?.toLowerCase() === 'no' ? 'no' : 'yes';
}

function normalizeTradeAction(value: string | null): TradeAction {
  return value?.toLowerCase() === 'sell' ? 'Sell' : 'Buy';
}

function formatTraderAddress(address: string) {
  const trimmed = address.trim();

  if (trimmed.length <= 12) return trimmed || '--';

  return `${trimmed.slice(0, 6)}...${trimmed.slice(-4)}`;
}

function formatRelativeTradeTime(value: string) {
  const time = new Date(value).getTime();

  if (!Number.isFinite(time)) return '--';

  const diffMs = Date.now() - time;
  if (diffMs < 0) return 'now';

  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  return `${days}d`;
}

function formatTradeTime(value: string) {
  const date = new Date(value);

  if (!Number.isFinite(date.getTime())) return value || '--';

  const year = date.getFullYear();
  const month = padDatePart(date.getMonth() + 1);
  const day = padDatePart(date.getDate());
  const hours = padDatePart(date.getHours());
  const minutes = padDatePart(date.getMinutes());

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

function padDatePart(value: number) {
  return String(value).padStart(2, '0');
}

function formatPrice(value: number) {
  if (!Number.isFinite(value)) return '--';

  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

function formatCurrency(value: number) {
  if (!Number.isFinite(value)) return '--';

  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: value >= 1 ? 2 : 4,
  });
}

function formatShares(value: number) {
  if (!Number.isFinite(value)) return '--';

  return value.toLocaleString('en-US', {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  });
}
